import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCard } from '@angular/material/card';
import {
  MatFormField,
  MatLabel,
  MatSuffix,
} from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CustomTooltipModule } from '@jfw/angular/ecommerce/common-components/custom-tooltip';
import {
  PasswordFormControls,
  PasswordFormSchema,
  ZodFormUtilities,
} from '@jfw/angular/ecommerce/zod-forms';
import { DealerUserInviteResponse } from '@jfw/types';
import {
  catchError,
  combineLatest,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { AuthService } from '../../../services/auth/auth.service';
import { DealerUserService } from '../../../services/dealer-user/dealer-user.service';

type VerifyInviteResponse = {
  success: boolean;
  body: DealerUserInviteResponse;
};

type RegisterResponse = {
  success: boolean;
  message: string;
};

@Component({
  selector: 'app-register-user',
  templateUrl: './register-user.component.html',
  styleUrls: ['./register-user.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatCard,
    MatButton,
    RouterLink,
    ReactiveFormsModule,
    CustomTooltipModule,
    MatIcon,
    MatFormField,
    MatLabel,
    MatInput,
    MatSuffix,
    MatProgressSpinner,
  ],
})
export class RegisterUserComponent {
  private authService = inject(AuthService);
  private dealerUserService = inject(DealerUserService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  private firestoreId$ = this.route.paramMap.pipe(
    map((paramMap) => paramMap.get('firestoreId')),
  );

  private email$ = this.route.paramMap.pipe(
    map((paramMap) => paramMap.get('email')),
  );

  private verifyInviteResponse$: Observable<VerifyInviteResponse | null> =
    combineLatest([this.firestoreId$, this.email$]).pipe(
      take(1),
      switchMap(([firestoreId, email]) => {
        if (firestoreId && email) {
          return this.dealerUserService.verifyInvite(firestoreId, email).pipe(
            map((response) => {
              if (
                response.actionCode === 'doc-userid-ready-goto-login' ||
                response.actionCode === 'doc-userid-set-goto-login'
              ) {
                this.router.navigate(['/sign-in'], { state: { email: email } });
                return null;
              } else if (
                response.actionCode === 'no-auth-exists-goto-register'
              ) {
                return { success: true, body: response };
              } else {
                return { success: false, body: response };
              }
            }),
          );
        }
        return of(null);
      }),
    );

  public verifyInviteResponse = toSignal(this.verifyInviteResponse$);

  public hidePassword = true;

  public passwordForm = new FormGroup<PasswordFormControls>(
    {
      newPassword: new FormControl('', {
        validators: [Validators.required],
        nonNullable: true,
      }),
      confirmNewPassword: new FormControl('', {
        validators: [Validators.required],
        nonNullable: true,
      }),
    },
    [ZodFormUtilities.zodFormGroupValidator(PasswordFormSchema)],
  );

  private newPassword$ = new Subject<string>();

  public onPasswordFormSubmit(): void {
    if (this.passwordForm.invalid) return;
    const newPassword = this.passwordForm.controls.newPassword.value;
    this.newPassword$.next(newPassword);
  }

  public spinner = signal(false);

  private registerResponse$: Observable<RegisterResponse | null> =
    this.newPassword$.asObservable().pipe(
      (password) =>
        combineLatest([this.firestoreId$, this.email$, password]).pipe(
          take(1),
          tap(() => this.spinner.set(true)),
          switchMap(([firestoreId, email, password]) => {
            if (firestoreId && email && password) {
              return combineLatest([
                of(email),
                of(password),
                this.dealerUserService.registerDealerUser(
                  firestoreId,
                  password,
                ),
              ]).pipe(
                switchMap(([email, password, registerResponse]) =>
                  this.authService.signInWithEmailAndPassword(email, password),
                ),
                switchMap((response) => {
                  if (response.success) {
                    return this.authService
                      .sendEmailVerification()
                      .pipe(tap(() => this.spinner.set(false)));
                  } else {
                    this.spinner.set(false);
                    return of({
                      success: false,
                      message: response.message,
                    });
                  }
                }),
              );
            } else {
              this.spinner.set(false);
              return of({
                success: false,
                message: 'Missing firestoreId, email, or password.',
              });
            }
          }),
        ),
      catchError((error) => {
        console.error(error);
        return of({
          success: false,
          message: 'An unknown error occurred.',
        });
      }),
    );

  public registerResponse = toSignal(this.registerResponse$);
}
